import {
  AccessContext,
  DateTimeField,
  EnumField,
  NumberField,
  RemoteDataSource,
  Required,
  Schema,
  TextField
} from "lego-gui-lib";
import { createContext } from "react";
import { mainStore } from "../../core/mainStore";
import { ServiceProfileFK } from "../serviceProfile/schema";
import { CustomerFK } from "../customer/schema";
import { t } from "../../core/i18n/i18n";
import { get } from "lodash";
import {
  CustomerMessagesFK,
  CustomerMessagesFKNotRequired
} from "../message/schema";
import {
  ListFKException,
  ListFKPrimaryIn,
  ListFKPrimaryOut
} from "../list/schema";
import { LanguageFK } from "../language/schema";
import { LanguageSelectionFKNotRequired } from "../languageSelection/schema";
import { serviceProfileAllows } from "../serviceProfile/validator";
import { RoutingType, RoutingTypeEnum } from "./RoutingTypeEnum";
import { Auth } from "../../core/model/auth";
import { BooleanField, withExternalId } from "../../common/common";
import { AreaSplittingFKNotRequired } from "../areaSplitting/schema";
import { CalendarFKNotRequired } from "../calendar/schema";
import { BarringMatrixFKNotRequired } from "../barringMatrix/schema";
import { ContextAwareListDataSource } from "../../common/ContextAwareListDataSource";
import { FormState } from "final-form";
import { RangeIncOrEmpty } from "../../common/validators";
import { DynamicColumn } from "lego-gui-lib/lib";
import { ForeignKeyFieldWithPageSize } from "../../common/ForeignKeyFieldWithPageSize";

const auth = mainStore.auth;

export const PresentationValues = [
  {
    label: t("hns:san:presentationEnum:original"),
    value: 0
  },
  {
    label: t("hns:san:presentationEnum:mainNumber"),
    value: 1
  }
];

export const CallLogValues = [
  {
    label: t("hns:san:callLogEnum:inactive"),
    value: 0
  },
  {
    label: t("hns:san:callLogEnum:active"),
    value: 1
  },
  {
    label: t("hns:san:callLogEnum:activeAnonymization"),
    value: 2
  }
];

const onQueueActiveChange = (
  newValue: any,
  oldValue: any,
  formState: FormState<any>
) => {
  if (newValue == 0) {
    formState.values["queueConfigRef"] = null;
  }
};

export const SANSchema = new Schema<DynamicColumn>({
  groups: {
    "common:main": t("common:main"),
    "common:general": t("common:general"),
    "hns:serviceProfile:domain": t("hns:serviceProfile:domain"),
    "hns:san:reportingActive": t("hns:san:reportingActive"),
    "hns:san:adminNotes": t("hns:san:adminNotes"),
    "hns:san:temporaryDeactivation": t("hns:san:temporaryDeactivation"),
    "hns:san:billingInformation": t("hns:san:billingInformation"),
    "hns:san:callBlocking": t("hns:san:callBlocking"),
    "hns:san:callLimiting": t("hns:san:callLimiting"),
    "hns:san:callRouting": t("hns:san:callRouting"),
    "hns:san:callQueueing": t("hns:san:callQueueing"),
    "hns:san:priceInformation": t("hns:san:priceInformation"),
    "hns:san:incomingCalls": t("hns:san:incomingCalls"),
    "hns:san:outgoingCalls": t("hns:san:outgoingCalls"),
    "hns:san:numberOfCalls": t("hns:san:numberOfCalls"),
    "hns:san:durationRelated": t("hns:san:durationRelated"),
    "hns:san:smsNotifications": t("hns:san:smsNotifications"),
    "hns:san:reporting": t("hns:san:reporting"),
    "hns:san:accessByPin": t("hns:san:accessByPin"),
    "hns:languageSelect:domain": t("hns:languageSelect:domain"),
    "hns:san:presentation": t("hns:san:presentation"),
    "hns:san:aNumberPortability": t("hns:san:aNumberPortability"),
    "hns:san:greetings": t("hns:san:greetings"),
    "hns:san:numberValidationOnGui": t("hns:san:numberValidationOnGui"),
    "hns:san:networkRelatedConfig": t("hns:san:networkRelatedConfig")
  },
  fields: [
    // private Integer   customerRef;
    {
      name: "customerRef",
      title: t("hns:customer:domain"),
      type: CustomerFK,
      group: "common:main",
      sort: { field: "externalId" },
      if: () => auth.isAdmin()
    },
    // private String    mainNumber;
    {
      name: "mainNumber",
      title: t("hns:san:mainNumber"),
      type: TextField(Required),
      group: "common:main",
      readonly: () => !auth.isAdmin() && !auth.isPlatformAdmin()
    },
    // private String    genericNote1; -- super admin --
    {
      name: "genericNote1",
      title: t("hns:san:genericNote1"),
      type: TextField(),
      if: () => auth.isAdmin(),
      group: "hns:san:adminNotes"
    },
    // private String    genericNote2; -- super admin --
    {
      name: "genericNote2",
      title: t("hns:san:genericNote2"),
      type: TextField(),
      if: () => auth.isAdmin(),
      group: "hns:san:adminNotes"
    },
    // private String    genericNote3; -- super admin --
    {
      name: "genericNote3",
      title: t("hns:san:genericNote3"),
      type: TextField(),
      if: () => auth.isAdmin(),
      group: "hns:san:adminNotes"
    },
    // private Integer   serviceProfileRef; -- super admin --
    {
      name: "serviceProfileRef",
      title: t("hns:serviceProfile:domain"),
      type: ServiceProfileFK,
      if: () => auth.isAdmin(),
      group: "hns:serviceProfile:domain"
    },
    // private Byte      blocked;
    {
      name: "blocked",
      title: t("common:blocked"),
      type: BooleanField(Required),
      group: "common:main",
      readonly: () => !auth.isAdmin()
    },
    // private Timestamp deactivationStart;
    {
      name: "deactivationStart",
      title: t("hns:san:deactivationStart"),
      type: DateTimeField(),
      group: "hns:san:temporaryDeactivation",
      if: (ctx: AccessContext) =>
        serviceProfileAllows(ctx, "featSanDeactivation"),
      onChange: (newValue, oldValue, formState) => {
        if (!newValue) {
          formState.values.deactivationEnd = null;
          formState.values.deactivationMsgRef = null;
        }
      }
    },
    // private Timestamp deactivationEnd;
    {
      name: "deactivationEnd",
      title: t("hns:san:deactivationEnd"),
      type: DateTimeField(Required),
      if: (ctx: AccessContext) =>
        get(ctx, "data.deactivationStart") &&
        serviceProfileAllows(ctx, "featSanDeactivation"),
      group: "hns:san:temporaryDeactivation"
    },
    // private Integer   deactivationMsgRef;
    {
      name: "deactivationMsgRef",
      title: t("hns:san:deactivationMsgRef"),
      type: CustomerMessagesFK,
      if: (ctx: AccessContext) =>
        get(ctx, "data.deactivationStart") &&
        serviceProfileAllows(ctx, "featSanDeactivation"),
      group: "hns:san:temporaryDeactivation"
    },
    // private String    billingSanType; -- super admin --
    {
      name: "billingSanType",
      title: t("hns:san:billingSanType"),
      type: TextField(Required),
      if: () => auth.isAdmin(),
      group: "hns:san:billingInformation"
    },
    // private String    billingSystem; -- super admin --
    {
      name: "billingSystem",
      title: t("hns:san:billingSystem"),
      type: TextField(Required),
      if: () => auth.isAdmin(),
      group: "hns:san:billingInformation"
    },
    // private String    languageRef;
    {
      name: "languageRef",
      title: t("hns:san:defaultLanguage"),
      type: LanguageFK,
      group: "common:general"
    },
    // private Integer   noAnswerTimeout;
    {
      name: "noAnswerTimeout",
      title: t("hns:san:noAnswerTimeout"),
      type: NumberField(RangeIncOrEmpty(5000, 150000)),
      group: "common:general"
    },
    // private Byte      unidentifiedCallerBlocked;
    {
      name: "unidentifiedCallerBlocked",
      title: t("hns:san:unidentifiedCallerBlocked"),
      type: BooleanField(Required),
      group: "hns:san:incomingCalls",
      if: (ctx: AccessContext) =>
        serviceProfileAllows(ctx, "featUnIdentifiedCallersBlock")
    },
    // private Integer   unidentifiedCallerMsgRef;
    {
      name: "unidentifiedCallerMsgRef",
      title: t("hns:san:unidentifiedCallerMsgRef"),
      type: CustomerMessagesFK,
      if: (ctx: AccessContext) =>
        get(ctx, "data.unidentifiedCallerBlocked") &&
        serviceProfileAllows(ctx, "featUnIdentifiedCallersBlock"),
      group: "hns:san:incomingCalls"
    },
    // private Byte      aNumberPortability; -- super admin --
    {
      name: "anumberPortability",
      title: t("hns:san:aNumberPortability"),
      type: BooleanField(Required),
      if: (ctx: AccessContext) =>
        auth.isAdmin() && serviceProfileAllows(ctx, "featANumPortability"),
      group: "hns:san:aNumberPortability"
    },
    // private Integer   areaSplittingRef;
    {
      name: "areaSplittingRef",
      title: t("hns:areaSplitting:domain"),
      type: AreaSplittingFKNotRequired,
      group: "common:general"
    },
    // private Integer   calendarRef;
    {
      name: "calendarRef",
      title: t("hns:calendar:domain"),
      type: CalendarFKNotRequired,
      group: "common:general"
    },
    // private Byte      screenInMobile;
    {
      name: "screenInMobile",
      title: t("hns:san:screenInMobile"),
      type: BooleanField(Required),
      group: "hns:san:incomingCalls",
      if: (ctx: AccessContext) => serviceProfileAllows(ctx, "featScreenIn")
    },
    // private Byte      screenInForeign;
    {
      name: "screenInForeign",
      title: t("hns:san:screenInForeign"),
      type: BooleanField(Required),
      group: "hns:san:incomingCalls",
      if: (ctx: AccessContext) => serviceProfileAllows(ctx, "featScreenIn")
    },
    // private Integer   screenInPrimaryListRef;
    {
      name: "screenInPrimaryListRef",
      title: t("hns:san:screenInPrimaryListRef"),
      type: ListFKPrimaryIn,
      group: "hns:san:incomingCalls",
      if: (ctx: AccessContext) => serviceProfileAllows(ctx, "featScreenIn")
    },
    // private Integer   screenInExceptionListRef;
    {
      name: "screenInExceptionListRef",
      title: t("hns:san:screenInExceptionListRef"),
      type: ListFKException,
      group: "hns:san:incomingCalls",
      if: (ctx: AccessContext) => serviceProfileAllows(ctx, "featScreenIn")
    },
    // private Byte      screenOutForeign;
    {
      name: "screenOutForeign",
      title: t("hns:san:screenOutForeign"),
      type: BooleanField(Required),
      group: "hns:san:outgoingCalls",
      if: (ctx: AccessContext) => serviceProfileAllows(ctx, "featScreenOut")
    },
    // private Integer   screenOutPrimaryListRef;
    {
      name: "screenOutPrimaryListRef",
      title: t("hns:san:screenOutPrimaryListRef"),
      type: ListFKPrimaryOut,
      group: "hns:san:outgoingCalls",
      if: (ctx: AccessContext) => serviceProfileAllows(ctx, "featScreenOut")
    },
    // private Integer   screenOutExceptionListRef;
    {
      name: "screenOutExceptionListRef",
      title: t("hns:san:screenOutExceptionListRef"),
      type: ListFKException,
      group: "hns:san:outgoingCalls",
      if: (ctx: AccessContext) => serviceProfileAllows(ctx, "featScreenOut")
    },
    // private Integer   maxCalls;
    {
      name: "maxCalls",
      title: t("hns:san:maxCalls"),
      type: NumberField(Required),
      group: "hns:san:numberOfCalls",
      if: (ctx: AccessContext) => serviceProfileAllows(ctx, "featMaxCallsSan")
    },
    // private Integer   maxDuration;
    {
      name: "maxDuration",
      title: t("hns:san:maxDuration"),
      type: NumberField(),
      group: "hns:san:durationRelated",
      if: (ctx: AccessContext) =>
        serviceProfileAllows(ctx, "featMaxCallDuration")
    },
    // private Byte      maxDurationAllowMsg;
    {
      name: "maxDurationAllowMsg",
      title: t("hns:san:maxDurationAllowMsg"),
      type: BooleanField(Required),
      group: "hns:san:durationRelated",
      if: (ctx: AccessContext) =>
        serviceProfileAllows(ctx, "featMaxCallDuration")
    },
    // private Integer   msgPriceAnnRef;
    {
      name: "msgPriceAnnRef",
      title: t("hns:san:msgPriceAnnRef"),
      type: CustomerMessagesFKNotRequired,
      group: "hns:san:priceInformation",
      if: (ctx: AccessContext) =>
        serviceProfileAllows(ctx, "featInteractPriceAnn")
    },
    // private String    pin;
    {
      name: "pin",
      title: t("hns:san:pin"),
      type: TextField(),
      group: "hns:san:accessByPin",
      if: (ctx: AccessContext) => serviceProfileAllows(ctx, "featInteractPin")
    },
    // private Integer   msgPinRef;
    {
      name: "msgPinRef",
      title: t("hns:san:msgPinRef"),
      type: CustomerMessagesFKNotRequired,
      if: ctx =>
        get(ctx, "data.pin") && serviceProfileAllows(ctx, "featInteractPin"),
      group: "hns:san:accessByPin"
    },
    // private Integer   msgWelcomeAnnRef;
    {
      name: "msgWelcomeAnnRef",
      title: t("hns:san:msgWelcomeAnnRef"),
      type: CustomerMessagesFKNotRequired,
      group: "hns:san:greetings",
      if: (ctx: AccessContext) =>
        serviceProfileAllows(ctx, "featInteractWelcomeAnn")
    },
    // private Integer   languageSelectRef;
    {
      name: "languageSelectRef",
      title: t("hns:languageSelect:domain"),
      type: LanguageSelectionFKNotRequired,
      group: "hns:languageSelect:domain",
      if: (ctx: AccessContext) =>
        serviceProfileAllows(ctx, "featInteractSelectLang")
    },
    // private Byte      routingType;
    {
      name: "routingType",
      title: t("hns:san:routingType"),
      type: RoutingTypeEnum,
      group: "hns:san:callRouting",
      onChange: (newValue, oldValue, formState) => {
        formState.values["translatedDestination"] = undefined;
        formState.values["msgRoutingRef"] = undefined;
      }
    },
    // private String    translatedDestination;
    {
      name: "translatedDestination",
      title: t("hns:san:translatedDestination"),
      type: TextField(Required),
      if: ctx => get(ctx, "data.routingType") == RoutingType.TRANSLATION,
      group: "hns:san:callRouting"
    },
    // private Integer   msgRoutingRef;
    {
      name: "msgRoutingRef",
      title: t("hns:san:msgRoutingRef"),
      type: CustomerMessagesFK,
      if: ctx => get(ctx, "data.routingType") == RoutingType.ANNOUNCEMENT,
      group: "hns:san:callRouting"
    },
    // private Byte      queueActive;
    {
      name: "queueActive",
      title: t("hns:san:queueActive"),
      type: BooleanField(Required),
      group: "hns:san:callQueueing",
      if: ctx => serviceProfileAllows(ctx, "featQueue"),
      onChange: onQueueActiveChange
    },
    // private Integer   queueConfigRef;
    {
      name: "queueConfigRef",
      title: t("hns:queueConfig:domain"),
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      type: () => require("../queueConfig/schema").QueueConfigurationFK,
      if: ctx =>
        get(ctx, "data.queueActive") && serviceProfileAllows(ctx, "featQueue"),
      group: "hns:san:callQueueing"
    },
    // private Byte      presentation;
    {
      name: "presentation",
      title: t("hns:san:presentation"),
      type: EnumField(PresentationValues, Required),
      group: "hns:san:presentation"
    },
    // private Byte      addRedirInfo; -- super admin --
    {
      name: "addRedirInfo",
      title: t("hns:san:addRedirInfo"),
      type: BooleanField(Required),
      if: () => auth.isAdmin(),
      group: "hns:san:networkRelatedConfig"
    },
    // private String    outPrefix; -- super admin --
    {
      name: "outPrefix",
      title: t("hns:san:outPrefix"),
      type: TextField(),
      if: () => auth.isAdmin(),
      group: "hns:san:networkRelatedConfig"
    },
    // private String    crPrefix; -- super admin --
    {
      name: "crPrefix",
      title: t("hns:san:crPrefix"),
      type: TextField(),
      if: () => auth.isAdmin(),
      group: "hns:san:networkRelatedConfig"
    },
    // private Byte      smsNotifActive;
    {
      name: "smsNotifActive",
      title: t("hns:san:smsNotifActive"),
      type: BooleanField(Required),
      group: "common:general",
      if: (ctx: AccessContext) => serviceProfileAllows(ctx, "featSmsNotif")
    },
    // private String    smsNotifDestination;
    {
      name: "smsNotifDestination",
      title: t("hns:san:smsNotifDestination"),
      type: NumberField(),
      if: ctx =>
        get(ctx, "data.smsNotifActive") &&
        serviceProfileAllows(ctx, "featSmsNotif"),
      group: "common:general"
    },
    // private Integer   barringMatrixRef; -- super admin --
    {
      name: "barringMatrixRef",
      title: t("hns:barringMatrix:domain"),
      type: BarringMatrixFKNotRequired,
      if: () => auth.isAdmin(),
      group: "hns:san:numberValidationOnGui"
    },
    // private Byte      callLog; -- super admin --
    {
      name: "callLog",
      title: t("hns:callLog:domain"),
      type: EnumField(CallLogValues, Required),
      if: (ctx: AccessContext) =>
        auth.isAdmin() && serviceProfileAllows(ctx, "featCallLog"),
      group: "common:general"
    },
    // private Byte      reportingActive; -- super admin --
    {
      name: "reportingActive",
      title: t("hns:san:reportingActive"),
      type: BooleanField(Required),
      if: (ctx: AccessContext) =>
        auth.isAdmin() && serviceProfileAllows(ctx, "featReporting"),
      group: "common:general"
    },
    // private String    ringingAnnUrl; -- super admin --
    {
      name: "ringingAnnUrl",
      title: t("hns:san:ringingAnnUrl"),
      type: TextField(),
      if: () => auth.isAdmin(),
      group: "hns:san:networkRelatedConfig"
    },
    {
      name: "directB2bCallTreatment",
      title: t("hns:san:directB2bCallTreatment"),
      type: BooleanField(Required),
      if: () => auth.isAdmin(),
      group: "hns:san:networkRelatedConfig"
    }
  ],
  defaultSort: { sort: "mainNumber", order: "asc" },
  primaryKey: ["id"],
  security: {
    readableRole: Auth.Roles.ANYONE,
    editableRole: Auth.Roles.ANYONE_WITH_WRITE
  }
});
export const SANEditSchema = SANSchema.extend({
  customerRef: { readonly: true }
});

mainStore.SAN = RemoteDataSource.create({
  api: mainStore.api,
  name: "SAN",
  schema: SANSchema,
  url: "/sans",
  commons: mainStore.commons
});

mainStore.SAN.oneDataSource.onEvent("one.load.success", function() {
  const customerRef = get(mainStore.SAN.oneDataSource.one, "customerRef");
  mainStore.customer.oneDataSource.findOne({ id: customerRef });
});

export const SanContext = createContext(mainStore.SAN);

mainStore.sanFk = ContextAwareListDataSource.clone(
  mainStore.SAN.listDataSource,
  {
    observableFieldRef: "customerRef",
    observableDataSource: "customer"
  }
);

export const sanCustomerContext = createContext(mainStore.sanFk);

export const SANFK = ForeignKeyFieldWithPageSize(
  {
    dataSource: mainStore.sanFk,
    searchField: "mainNumber",
    valueField: "id",
    labelField: withExternalId("mainNumber"),
    pageSize: 30000
  },
  Required
);

mainStore.sanFkOutOfContext = RemoteDataSource.clone(mainStore.SAN);

export const SANFKOutOfContext = ForeignKeyFieldWithPageSize(
  {
    dataSource: mainStore.sanFkOutOfContext.listDataSource,
    searchField: "mainNumber",
    valueField: "id",
    labelField: withExternalId("mainNumber"),
    pageSize: 30000
  },
  Required
);
